import type { App, Component } from "vue";

type VueIslandConfig = Record<string, () => Promise<Component>>;

function initVueIslands(
  components: VueIslandConfig,
  appCallback?: (app: App) => void,
) {
  renderVueIslands(components, appCallback);
  const observer = new MutationObserver(() => {
    renderVueIslands(components, appCallback);
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function renderVueIslands(
  components: VueIslandConfig,
  appCallback?: (app: App) => void,
) {
  // We extract the name here so we can remove the attribute before we do something async
  // This prevents trying to render twice on the same container
  document
    .querySelectorAll<HTMLElement>("[data-component]")
    .forEach((container) => {
      const name = container.dataset.component!;
      const loading = container.dataset.loading;
      delete container.dataset.component;

      if (loading === "eager") {
        void loadComponent(name, components, container, appCallback);

        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) {
            observer.disconnect();
            void loadComponent(name, components, container, appCallback);
          }
        },
        { rootMargin: "200px" },
      );

      observer.observe(container);
    });
}

async function loadComponent(
  name: string,
  components: VueIslandConfig,
  container: HTMLElement,
  appCallback?: (app: App) => void,
): Promise<void> {
  const { renderVueIsland } = await import("@/lib/helpers/vueIsland");

  renderVueIsland(name, container, components[name], appCallback);
}

export { initVueIslands };
export type { VueIslandConfig };
